import { Head } from "@inertiajs/react"
import { ChevronLeft, Users } from "lucide-react"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { CommunityFeedGrid } from "@/components/missions/community-feed-grid"
import { CommunityFeedSkeleton } from "@/components/missions/community-feed-skeleton"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import type { CreatorMissionCommunityProps } from "@/types/mission-community"

/**
 * Creator/Missions/Community — single-mission community feed (W11, OKS-392).
 *
 * A 3-column grid of up to 12 APPROVED submissions for one mission — intra-
 * brand social proof nudging the creator to post their own content. Lives
 * inside `AppLayout` (creator-shell). Read-only; the whole tile links out to
 * TikTok.
 *
 * States (UX Spec §3):
 *   - loading — `CommunityFeedSkeleton` (12 placeholder tiles; test-only seam,
 *     no spinner). The not-found / cross-brand-404 case is handled SERVER-side
 *     (`errors#not_found`), never a page state — so there's no error branch.
 *   - empty   — 0 approved posts → `EmptyState` ("Be the first to post").
 *   - success — 1–12 tiles, 4th row clipped at ≥10 (handled by the grid).
 *
 * Brand isolation is enforced upstream: a cross-brand / non-existent mission
 * id 404s in `CreatorMissionCommunityQuery` before this page ever renders.
 */
type Props = CreatorMissionCommunityProps & {
  /** Test-only seam — render the loading skeleton. Never forwarded to users. */
  _forceLoading?: boolean
}

export default function CreatorMissionCommunity({
  mission,
  posts,
  _forceLoading = false,
}: Props) {
  const { t } = useTranslation()
  const headingRef = useRef<HTMLHeadingElement>(null)

  // Focus the page heading on land (a11y per UX Spec §6).
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <AppLayout>
      <Head
        title={t("creator_mission_community.document_title", {
          mission: mission.title,
        })}
      />

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-4">
        <header className="flex flex-col gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
            data-testid="community-back"
            className="text-muted-foreground -ml-2 h-8 w-fit gap-1 px-2"
            aria-label={t("creator_mission_community.header.back")}
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
            {t("creator_mission_community.header.back")}
          </Button>

          <div className="flex flex-col gap-0.5">
            <h1
              ref={headingRef}
              tabIndex={-1}
              data-testid="community-heading"
              className="text-xl font-semibold tracking-tight outline-none"
            >
              {mission.title}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("creator_mission_community.header.subtitle")}
            </p>
          </div>
        </header>

        {_forceLoading ? (
          <CommunityFeedSkeleton />
        ) : posts.length === 0 ? (
          <EmptyState
            icon={Users}
            title={t("creator_mission_community.empty.title")}
            description={t("creator_mission_community.empty.body")}
            data-testid="community-empty"
          />
        ) : (
          <CommunityFeedGrid posts={posts} />
        )}
      </div>
    </AppLayout>
  )
}
